import React from 'react';

interface AuthVisualPanelProps {
  appName: string;
}

export default function AuthVisualPanel({ appName }: AuthVisualPanelProps) {
  return (
    <div className="w-full gradient-dark flex flex-col items-center justify-center p-12 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large orb top-right */}
        <div
          className="absolute -top-20 -right-20 w-80 h-80 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, #3977FF 0%, transparent 70%)' }}
        />
        {/* Medium orb bottom-left */}
        <div
          className="absolute -bottom-10 -left-10 w-60 h-60 rounded-full opacity-15"
          style={{ background: 'radial-gradient(circle, #8B5CF6 0%, transparent 70%)' }}
        />
        {/* Grid lines */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Central GPU Node Illustration */}
      <div className="relative z-10 flex flex-col items-center text-center">
        {/* Hexagonal node graphic */}
        <div className="relative mb-8">
          {/* Outer glow ring */}
          <div className="absolute inset-0 rounded-full animate-pulse-slow"
            style={{ background: 'radial-gradient(circle, rgba(57,119,255,0.3) 0%, transparent 70%)' }}
          />

          {/* Main hex container */}
          <div className="relative w-40 h-40 flex items-center justify-center">
            {/* SVG hex node graphic */}
            <svg viewBox="0 0 160 160" className="w-full h-full animate-float" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Outer connector lines */}
              <line x1="80" y1="80" x2="20" y2="30" stroke="#6EA8FF" strokeWidth="1.5" strokeOpacity="0.6" />
              <line x1="80" y1="80" x2="140" y2="30" stroke="#6EA8FF" strokeWidth="1.5" strokeOpacity="0.6" />
              <line x1="80" y1="80" x2="20" y2="130" stroke="#6EA8FF" strokeWidth="1.5" strokeOpacity="0.6" />
              <line x1="80" y1="80" x2="140" y2="130" stroke="#6EA8FF" strokeWidth="1.5" strokeOpacity="0.6" />
              <line x1="80" y1="80" x2="80" y2="10" stroke="#8B5CF6" strokeWidth="1.5" strokeOpacity="0.6" />
              <line x1="80" y1="80" x2="80" y2="150" stroke="#8B5CF6" strokeWidth="1.5" strokeOpacity="0.6" />

              {/* Satellite nodes */}
              <circle cx="20" cy="30" r="8" fill="#3977FF" fillOpacity="0.7" />
              <circle cx="140" cy="30" r="8" fill="#3977FF" fillOpacity="0.7" />
              <circle cx="20" cy="130" r="8" fill="#6EA8FF" fillOpacity="0.7" />
              <circle cx="140" cy="130" r="8" fill="#6EA8FF" fillOpacity="0.7" />
              <circle cx="80" cy="10" r="6" fill="#8B5CF6" fillOpacity="0.8" />
              <circle cx="80" cy="150" r="6" fill="#8B5CF6" fillOpacity="0.8" />

              {/* Central hexagon */}
              <polygon
                points="80,45 108,62 108,98 80,115 52,98 52,62"
                fill="url(#hexGrad)"
                stroke="#6EA8FF"
                strokeWidth="2"
              />

              {/* Inner detail lines */}
              <line x1="80" y1="45" x2="80" y2="115" stroke="white" strokeWidth="0.5" strokeOpacity="0.3" />
              <line x1="52" y1="62" x2="108" y2="98" stroke="white" strokeWidth="0.5" strokeOpacity="0.3" />
              <line x1="108" y1="62" x2="52" y2="98" stroke="white" strokeWidth="0.5" strokeOpacity="0.3" />

              {/* Center dot */}
              <circle cx="80" cy="80" r="6" fill="white" fillOpacity="0.9" />

              <defs>
                <linearGradient id="hexGrad" x1="52" y1="45" x2="108" y2="115" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#3977FF" />
                  <stop offset="100%" stopColor="#8B5CF6" />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>

        {/* Text */}
        <h1 className="text-3xl font-extrabold text-white mb-3 tracking-tight">
          {appName}
        </h1>
        <p className="text-base text-white/60 max-w-xs leading-relaxed mb-8">
          Earn passive USDT income through distributed GPU compute nodes. Deploy, collect, and grow.
        </p>

        {/* Feature pills */}
        <div className="flex flex-wrap justify-center gap-2">
          {[
            { label: 'GPU Compute Nodes', color: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
            { label: 'USDT Earnings', color: 'bg-green-500/20 text-green-300 border-green-500/30' },
            { label: 'BSC + TRON', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
            { label: '3-Level Referrals', color: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
          ].map((pill) => (
            <span
              key={`pill-${pill.label}`}
              className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border ${pill.color}`}
            >
              {pill.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}